import { BaseLRUStore } from 'generic/baseLRUStore';
import { Dispatch } from '@reduxjs/toolkit';
import { removeFocusLevelImagesAvailableKey } from 'redux/reducers/wellReducer';
import { FocusLevelImagesData } from './constants';

// Sized to keep a full 96-well plate's focus level images resident at once.
export const DEFAULT_FOCUS_LEVEL_CACHE_MAX_ENTRIES = 96;
export const DEFAULT_FOCUS_LEVEL_CACHE_MAX_SIZE_MB = 1024;

const mbToBytes = (mb: number): number => mb * 1024 * 1024;

const calculateFocusLevelImagesDataSize = (item: FocusLevelImagesData): number => {
  const size = item.data?.reduce((total, image) => total + (image.image?.length ?? 0), 0);
  return size || 1;
};

class FocusLevelImageStore extends BaseLRUStore<string, FocusLevelImagesData> {
  private static _instance: FocusLevelImageStore;
  private dispatch?: Dispatch;
  private readonly fetchesInFlight = new Set<string>();

  private constructor() {
    super({
      maxEntries: DEFAULT_FOCUS_LEVEL_CACHE_MAX_ENTRIES,
      maxTotalSize: mbToBytes(DEFAULT_FOCUS_LEVEL_CACHE_MAX_SIZE_MB),
      sizeCalculation: calculateFocusLevelImagesDataSize,
    });
  }

  public static getInstance(): FocusLevelImageStore {
    if (!FocusLevelImageStore._instance) {
      FocusLevelImageStore._instance = new FocusLevelImageStore();
    }
    return FocusLevelImageStore._instance;
  }

  public injectDispatch(dispatch: Dispatch) {
    if (!this.dispatch) {
      this.dispatch = dispatch;
    }
  }

  public isFetching(key: string): boolean {
    return this.fetchesInFlight.has(key);
  }

  public markFetching(key: string): void {
    this.fetchesInFlight.add(key);
  }

  public clearFetching(key: string): void {
    this.fetchesInFlight.delete(key);
  }

  protected override onEvict(key: string) {
    if (!this.dispatch) {
      console.warn(
        'Dispatch function is not set. Cannot remove focus level images from store.',
      );
      return;
    }
    this.dispatch(removeFocusLevelImagesAvailableKey({ key }));
  }
}

export default FocusLevelImageStore.getInstance() as FocusLevelImageStore;
